import { Fragment } from 'react';

import { Button } from '@/components/ui/button';
import { CollapsibleList } from '@/features/proactive-agents/CollapsibleList';
import { plural } from '@/features/proactive-agents/plural';
import type {
  UserChangeStatus,
  UserOutcome,
} from '@/features/proactive-agents/proactive-agents-types';
import type { ChangePhase } from '@/features/proactive-agents/useProactiveAgents';

type FinishedOutcome = Exclude<UserOutcome, 'waiting'>;

// In the order the lists show
const FINISHED_OUTCOMES: FinishedOutcome[] = ['failed', 'skipped', 'done'];

const OUTCOME_TITLES: Record<FinishedOutcome, string> = {
  failed: 'Failed, may work if tried again',
  skipped: 'Skipped',
  done: 'Done',
};

// What the status line says before a status has loaded
const NO_STATUS_TEXTS: Partial<Record<ChangePhase, string>> = {
  loading: 'loading its progress...',
  'not-found': 'not found, so it probably did not start',
  unavailable: 'its progress could not be loaded',
};

const summarize = ({ counts }: UserChangeStatus) => {
  const finished = FINISHED_OUTCOMES.map(
    (outcome) => `${counts[outcome].toLocaleString()} ${outcome}`
  ).join(', ');
  return counts.waiting
    ? `${finished}, ${counts.waiting.toLocaleString()} waiting`
    : finished;
};

// Failed users are listed one by one, since an admin acts on them. Done and skipped users are counted by detail.
const itemsOf = (outcome: FinishedOutcome, status: UserChangeStatus) =>
  outcome === 'failed'
    ? status.failed.map((result, index) => ({
        key: String(result.user_id ?? `no-user-${index}`),
        text: `${result.email ?? 'Unknown user'}: ${result.detail}`,
      }))
    : status.details
        .filter((detailCount) => detailCount.outcome === outcome)
        .map((detailCount) => ({
          key: detailCount.detail,
          text: `${plural(detailCount.user_count, 'user')}: ${detailCount.detail}`,
        }));

// A change runs in the background, and its results fill in as it reaches each user
export const UserChangeResults = ({
  name,
  phase,
  status,
  error,
  onDismiss,
}: {
  name: string;
  phase: ChangePhase;
  status: UserChangeStatus | undefined;
  error: Error | null;
  onDismiss: () => void;
}) => {
  return (
    <div className="grid gap-3 rounded-md bg-muted px-4 py-3">
      <p role="status" className="text-sm font-medium">
        {name}: {status ? summarize(status) : NO_STATUS_TEXTS[phase]}
      </p>
      {error && phase !== 'not-found' ? (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      ) : null}
      {phase === 'running' ? (
        <p className="text-xs text-muted-foreground">
          The change runs on the server and goes on if this page is closed. This
          updates every few seconds at first, then once a minute. Another change
          can be started here once this one has reached every user.
        </p>
      ) : null}
      {phase !== 'loading' && phase !== 'finished' ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Button type="button" size="sm" variant="outline" onClick={onDismiss}>
            Dismiss
          </Button>
          {phase === 'not-found'
            ? 'Forgets this change.'
            : 'Use only if this change seems stuck. If it still runs, another change may then act on the same users at the same time.'}
        </div>
      ) : null}
      {status
        ? FINISHED_OUTCOMES.map((outcome) => (
            <Fragment key={outcome}>
              <CollapsibleList
                title={OUTCOME_TITLES[outcome]}
                count={status.counts[outcome]}
                items={itemsOf(outcome, status)}
                open={outcome !== 'done'}
              />
              {outcome === 'failed' &&
              status.counts.failed > status.failed.length ? (
                <p className="text-xs text-muted-foreground">
                  The list shows the first{' '}
                  {status.failed.length.toLocaleString()} by email.
                </p>
              ) : null}
            </Fragment>
          ))
        : null}
    </div>
  );
};
